import React, { useState, useEffect, useCallback, useRef } from "react";
import { Modal } from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import theme from "../../theme";
import CommonButton from "../button";
import FeeRange from "../countries/FeeRange";
import {
  createCompanyFeesAction,
  updateCompanyFeesAction,
} from "../../containers/dashboard/action";

function getModalStyle() {
  const top = 50;
  const left = 50;

  return {
    top: `${top}%`,
    left: `${left}%`,
    transform: `translate(-${top}%, -${left}%)`,
  };
}

const useStyles = makeStyles(() => ({
  modalPaper: {
    position: "absolute",
  },
  container: {
    width: "460px",
    backgroundColor: theme.colors.White,
    boxShadow: "0px 0px 10px #CFD9EA80",
    borderRadius: "10px",
    padding: "40px 60px",
    maxHeight: "80vh",
    overflow: "auto",
    "&::-webkit-scrollbar-track": {
      marginLeft: "10px",
      background: theme.colors.Periwinkle,
      borderRadius: "0px 10px 10px 0px",
    },
    "&::-webkit-scrollbar-thumb": {
      background: theme.colors.DodgerBlue,
      borderRadius: "0px 10px 10px 0px",
      "& :hover": {
        background: theme.colors.Periwinkle,
      },
    },
    "&::-webkit-scrollbar": {
      width: 8,
    },
  },
  headerContainer: {
    color: theme.colors.DodgerBlue,
    fontSize: "24px",
    fontFamily: theme.fontFamily.MARKPROBOLD,
    marginBottom: "30px",
  },
  inputContainer: {
    marginBottom: "20px",
  },
  devider: {
    width: "100%",
    backgroundColor: theme.colors.Zumthor,
    height: "3px",
    marginTop: "30px",
    marginBottom: "30px",
  },
}));

// Create/update a company's fee ranges. Mirrors the vendor ShopFeesModal, but
// dispatches the company fee actions and stamps each new range with `company`.
function CompanyFeesModal({ openModal, editData, onClose, company_id = null }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [modalStyle] = useState(getModalStyle);

  const [feeRange, setFeeRange] = useState([{}]);
  const [feeRangeTabNumber, setFeeRangeTabNumber] = useState(1);
  const [isValidated, setIsValidated] = useState(false);
  const [editableData, setEditableData] = useState(null);
  const prevCreateSuccessRef = useRef(false);
  const prevUpdateSuccessRef = useRef(false);

  const dashboardReducer = useSelector((state) => {
    return state.dashboardReducer;
  });

  const createCompanyFeesMethod = useCallback(
    (data) => dispatch(createCompanyFeesAction(data)),
    [dispatch]
  );

  const updateCompanyFeesMethod = useCallback(
    (data) => dispatch(updateCompanyFeesAction(data)),
    [dispatch]
  );

  const onCloseModal = useCallback(() => {
    setFeeRange([{}]);
    setFeeRangeTabNumber(1);
    setIsValidated(false);
    setEditableData(null);
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (editData) {
      // If editData is an array, use it directly; otherwise wrap it in an array
      const editDataArray = Array.isArray(editData) ? editData : [editData];

      setFeeRangeTabNumber(editDataArray.length);

      const formattedData = editDataArray.map((item) => ({
        start_range: item.start_range || "",
        end_range: item.end_range || "",
        fee: item.fee || "",
        id: item.id,
      }));

      setFeeRange(formattedData);
      setEditableData(editDataArray);
    } else {
      setFeeRange([{}]);
      setFeeRangeTabNumber(1);
      setEditableData(null);
    }
  }, [editData]);

  useEffect(() => {
    const { createCompanyFeesSuccess, updateCompanyFeesSuccess } =
      dashboardReducer;

    const currentCreateSuccess = !!createCompanyFeesSuccess;
    const currentUpdateSuccess = !!updateCompanyFeesSuccess;

    // Only close modal if it's open and we have a new success state
    if (openModal) {
      if (currentCreateSuccess && !prevCreateSuccessRef.current) {
        prevCreateSuccessRef.current = true;
        onCloseModal();
      } else if (!currentCreateSuccess) {
        prevCreateSuccessRef.current = false;
      }

      if (currentUpdateSuccess && !prevUpdateSuccessRef.current) {
        prevUpdateSuccessRef.current = true;
        onCloseModal();
      } else if (!currentUpdateSuccess) {
        prevUpdateSuccessRef.current = false;
      }
    } else {
      // Reset refs when modal is closed
      if (!currentCreateSuccess) {
        prevCreateSuccessRef.current = false;
      }
      if (!currentUpdateSuccess) {
        prevUpdateSuccessRef.current = false;
      }
    }
  }, [dashboardReducer, onCloseModal, openModal]);

  const handleSubmit = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setIsValidated(true);

    let callApi = true;

    // Validate all fee ranges
    if (feeRange.length === feeRangeTabNumber) {
      feeRange.forEach((range) => {
        if (
          !range.hasOwnProperty("start_range") ||
          !range.hasOwnProperty("end_range") ||
          !range.hasOwnProperty("fee") ||
          range.start_range === "" ||
          range.end_range === "" ||
          range.fee === ""
        ) {
          callApi = false;
        }
      });
    } else {
      callApi = false;
    }

    if (callApi) {
      let returnList = [];
      feeRange.forEach((range) => {
        if (range.id) {
          // Update existing fee
          returnList.push({
            id: range.id,
            start_range: range.start_range,
            end_range: range.end_range,
            fee: range.fee,
          });
        } else {
          // Create new fee — each range carries its company id.
          const extras = {};
          if (company_id) {
            extras["company"] = company_id;
          }
          returnList.push({
            start_range: range.start_range,
            end_range: range.end_range,
            fee: range.fee,
            ...extras,
          });
        }
      });

      if (editData && editableData && editableData.some((item) => item.id)) {
        // Update existing fees
        updateCompanyFeesMethod(returnList);
      } else {
        // Create new fees
        createCompanyFeesMethod(returnList);
      }
    }
  };

  return (
    <Modal
      open={openModal}
      onClose={() => {
        onCloseModal();
      }}
    >
      <div style={modalStyle} className={classes.modalPaper}>
        <div className={classes.container}>
          <form autoComplete="off" onSubmit={handleSubmit}>
            <div>
              {!editData ? (
                <div className={classes.headerContainer}>Set Up Company Fees</div>
              ) : (
                <div className={classes.headerContainer}>Edit Company Fees</div>
              )}
            </div>
            {dashboardReducer?.createCompanyFeesError?.length ||
              dashboardReducer?.updateCompanyFeesError?.length ? (
              <div className={"error-container"}>
                {dashboardReducer?.createCompanyFeesError?.length
                  ? dashboardReducer.createCompanyFeesError.map(
                      (each, index) => <div key={index}>{each}</div>
                    )
                  : null}
                {dashboardReducer?.updateCompanyFeesError?.length
                  ? dashboardReducer.updateCompanyFeesError.map(
                      (each, index) => <div key={index}>{each}</div>
                    )
                  : null}
              </div>
            ) : null}

            <div className={classes.inputContainer}>
              {[...Array(feeRangeTabNumber)].map((e, i) => (
                <div key={i}>
                  {i > 0 && <div className={classes.devider}></div>}
                  <FeeRange
                    feeRange={feeRange}
                    setFeeRange={setFeeRange}
                    isValidated={isValidated}
                    feeRangeTabNumber={i}
                    editFeeRangeData={editableData}
                  />
                </div>
              ))}
            </div>

            {!editData && (
              <div>
                <CommonButton
                  text="Add Another Fee Range"
                  stylevariant="secondary2"
                  fontSize={"16px"}
                  icon={"/images/add.svg"}
                  onClick={() => {
                    setFeeRangeTabNumber(feeRangeTabNumber + 1);
                    setFeeRange([...feeRange, {}]);
                  }}
                />
              </div>
            )}

            <CommonButton
              type="submit"
              text={"Save"}
              stylevariant={"secondary4"}
              fontSize={"18px"}
              style={{
                marginTop: "20px",
                marginBottom: "20px",
                width: "80%",
                marginLeft: "10%",
              }}
            />
          </form>
        </div>
      </div>
    </Modal>
  );
}

export default CompanyFeesModal;
